import React, { useState, useEffect } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { ImageUpload, Popup } from '@helpers';
import { scale as initialScale, fullHeight, fullWidth } from '@components/frame';

import TouchableElement from '@components/common/touchableElement';
import Image from '@components/common/Image';

import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import ColorWrapper from '@styles/colors';
import Button from '@styles/buttons';

import Loading from '@components/loading';

import EditImage from './components/editImage';

import { KEYS_TAGS } from '@components/imageHandling/common';

import { updateUserPicture } from '@reducers/user';
import { IMAGE_UPLOAD_HEADER_TEXT } from '@v2/pages/common/constants/base.constants';

const ImageComponent = React.memo(
  ({ C, base64, action }) => {
    return (
      <Image
        {...{
          C,
          source: { uri: base64 || ' ' },
          resizeMode: 'cover',
          style: { width: '100%', height: '100%' },
          action,
        }}
      />
    );
  },
  (prev, next) => prev.base64 === next.base64
);

const UserImage = ({
  C,
  style = {},
  hideWhenEmpty,
  showEdit,
  showAdd,
  size = 100,
  updateUserPicture,
  user: { userInfo = {}, featureFlags = {} },
  hovered,
  pressed,
  onPress,
  scale = initialScale,
  font = initialFont,
}) => {
  const { userPicture = {}, userProfile = {}, userPictureLoaded } = userInfo;
  const { base64 } = userPicture || {};
  const { firstName = ' ', lastName = ' ', userId } = userProfile;

  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageLinkLoaded, setImageLinkLoaded] = useState(userPictureLoaded);

  useEffect(() => {
    (async () => {
      setUploadingImage(false);
      setImageLinkLoaded(true);
    })();
  }, [userPicture]);

  const showChangePhoto = featureFlags['settings-account-profile-picture'];

  const editAction = (isCropModeInit) => {
    Popup({
      bg: 'transparent',
      noPhoneHeight: fullHeight,
      width: fullWidth,
      offsetBottom: 0.1,
      offsetTop: 0.1,
      Component: (p) => (
        <EditImage
          {...{
            ...p,
            setUploadingImage,
            isCropModeInit,
          }}
        />
      ),
    });
  };

  if (hideWhenEmpty && imageLinkLoaded && !base64) {
    return null;
  }

  const useBigAddIcon = size > 60;

  const NoPhoto = () => (
    <View
      style={{
        justifyContent: 'center',
        alignContent: 'center',
        width: '100%',
        height: '100%',
        backgroundColor: C(hovered && !pressed ? 'Background/Light/hover menuWeb' : 'Label/Light/Unselected Web'),
      }}
    >
      <Text
        style={[
          {
            color: C(hovered && !pressed ? 'Label/Light/Unselected Web' : 'Background/Light/menuWeb'),
            textAlign: 'center',
          },
          font['Button 2'],
        ]}
      >
        {firstName ? firstName.charAt(0) : ''}
        {lastName ? lastName.charAt(0) : ''}
      </Text>
    </View>
  );

  return (
    <View style={style}>
      <TouchableElement
        onPress={async () => {
          if (onPress) {
            return onPress();
          }

          if (base64) {
            return editAction(true);
          }

          if (uploadingImage) {
            return;
          }
          ImageUpload({
            headerText: IMAGE_UPLOAD_HEADER_TEXT.AddUserImage,
            withoutAddExisting: true,
            uploadStart: () => setUploadingImage(true),
            uploadEnd: (img) => {
              setUploadingImage(false);
              updateUserPicture({ ...(userPicture || {}), base64: img.uri, localImage: true });
            },
            tags: [
              {
                scopeId: KEYS_TAGS.UserAccount,
                scopeReferenceName: userId,
                isFavorite: true,
              },
            ],
          });
        }}
      >
        {({ boxStyle, pressHoverColor }) => (
          <View
            style={[
              boxStyle,
              {
                width: size,
                height: size,
                borderRadius: size,
                backgroundColor: C(pressHoverColor('Button/Light/Secondary')),
                alignItems: 'center',
                overflow: 'hidden',
              },
            ]}
          >
            {showChangePhoto && imageLinkLoaded ? (
              base64 ? (
                <ImageComponent {...{ C, base64 }} />
              ) : uploadingImage ? (
                <Loading />
              ) : showAdd ? (
                <View style={{ alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                  <View style={{ paddingLeft: scale(useBigAddIcon ? 0 : 6) }}>
                    <Svg
                      icon={useBigAddIcon ? 'cameraBig' : 'cameraPlus'}
                      width={useBigAddIcon ? 39 : 31}
                      height={useBigAddIcon ? 30 : 22}
                      color={'Button/Light/Secondary Text'}
                    />
                  </View>
                  {useBigAddIcon && (
                    <Text style={[font['Button 2'], { color: C('Button/Light/Secondary Text'), marginTop: 8, userSelect: 'none' }]}>
                      {i18n.t('add photo')}
                    </Text>
                  )}
                </View>
              ) : (
                <NoPhoto />
              )
            ) : (
              <NoPhoto />
            )}
          </View>
        )}
      </TouchableElement>
      {showEdit && userInfo?.userPicture?.id && (
        <View style={{ marginTop: scale(12), flexDirection: 'row', justifyContent: 'center' }}>
          <Button
            {...{
              variant: 'S',
              type: 'Secondary',
              onPress: editAction,
            }}
          >
            {i18n.t('Edit')}
          </Button>
        </View>
      )}
    </View>
  );
};

export default connect(({ user }) => ({ user }), { updateUserPicture })(ColorWrapper(UserImage));
